import { useState } from "react";
import { createPortal } from "react-dom";
import { Modal } from "react-bootstrap";
import { KTIcon } from "../../../helpers";
import * as Yup from "yup";
import { useFormik } from "formik";
import clsx from "clsx";
import { addNewClient } from "../../../../app/modules/auth/core/_requests";
import { useSelector } from "react-redux";
import { AppState } from "../../../../redux/types";
import { useAuth } from "../../../../app/modules/auth/core/Auth";
import Swal from "sweetalert2";

type Props = {
	show: boolean;
	handleClose: () => void;
	setLoadClientList: (value: boolean) => void;
};
//NOTE portal render react children diffreant part of dom not inside the current dom tree
const modalsRoot = document.getElementById("root-modals") || document.body;

/* Validation Schema for the Modal form Using Formik and Yup and Typescript Formik+YUP+Typescript:*/
/* Form schema defined here */
const addClientSchema = Yup.object().shape({
	name: Yup.string()
		.min(3, "Minimum 3 characters")
		.max(50, "Maximum 50 symbols")
		.required("Name is required"),

	lastName: Yup.string()
		.min(3, "Minimum 3 characters")
		.max(50, "Maximum 50 symbols"),

	email: Yup.string()
		.matches(/^\S*$/, "Spaces are not allowed")
		.email("Invalid email format")
		.min(3, "Minimum 3 characters")
		.max(50, "Maximum 50 symbols"),

	contact: Yup.string()
		.matches(/^[0-9]*$/, "Invalid contact format")
		.min(10, "Minimum 10 characters")
		.max(13, "Maximum 13 characters"),

	username: Yup.string()
		.matches(/^\S*$/, "Spaces are not allowed")
		.min(4, "Minimum 4 characters")
		.max(20, "Maximum 20 characters")
		.required("Username is required"),

	password: Yup.string()
		.matches(
			/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&_-])[A-Za-z\d@$!%*?&_-]{8,}$/,
			"Invalid password format. Must contain at least 8 characters, one uppercase, one lowercase, one number and one special character"
		)
		.required("Password is required"),
	environment: Yup.string().matches(
		/(staging|production)/g,
		"Invalid environment format"
	),
});
/* Form schema defined here */

/* Initial Values for all the form elements*/
const initialValues = {
	name: "",
	lastName: "",
	email: "",
	contact: "",
	username: "",
	password: "",
	environment: "",
};
/*Initial Values for all the form elements*/

const AddNewClientModal = ({ show, handleClose, setLoadClientList }: Props) => {
	let token = useSelector((state: AppState) => state.token);
	const [loading, setLoading] = useState(false);
	const [environment, setEnviroment] = useState<string>("staging");
	const { logout } = useAuth();

	/*Form submission api integration*/
	const formik = useFormik({
		initialValues,
		validationSchema: addClientSchema,
		onSubmit: async (values, { setStatus, setSubmitting }) => {
			console.log(values);
			setLoading(true);
			try {
				if (token !== null) {
					// Ensure token is not null before calling addOperator
					const response = await addNewClient(
						token,
						values.name,
						values.lastName,
						values.email,
						values.contact.toString(),
						values.username,
						values.password,
						environment
					);

					const message = await response.data.message;

					Swal.fire({
						icon: "success",
						title: message,
						text: message,
					});
					closeModal();
					setLoadClientList(true);
					setLoading(false);
				}
			} catch (error: any) {
				let message = "";
				if (
					error?.response?.status === 403 ||
					error?.response?.status === "403"
				) {
					logout();
				}
				message = error.response?.data?.message || "An error occurred";
				if (error.code === "ERR_NETWORK") {
					message = "Network Error";
				}

				Swal.fire({
					icon: "error",
					title: message,
					text: message,
				});

				setStatus("The form details are incorrect");
				setSubmitting(false);
				setLoading(false);
			}
		},
	});
	/*Form submission api integration*/

	/* From Enviroment user Select */
	const handelEnviromentChange = (e: any) => {
		if (typeof e.target?.value === "string") {
			setEnviroment(e.target.value);
		}
	};
	/*Form Enviroment user Select */

	/* Reset The Form */
	const handleReset = () => {
		formik.resetForm();
		setLoading(false);
	};
	/* Reset the form */

	/* Close modal After from Submit or cancle */
	const closeModal = () => {
		handleClose();
		handleReset();
	};
	/* Close modal After from Submit or cancle */

	return (
		<>
			{createPortal(
				<Modal
					id="kt_modal_create_app"
					dialogClassName="modal-dialog modal-dialog-centered modal-dialog-scrollable mw-900px w-75"
					show={show}
					onHide={closeModal}
					backdrop={true}>
					<div className="modal-header">
						<h2>Add Client</h2>
						<div
							className="btn btn-sm btn-icon btn-active-color-primary"
							onClick={() => closeModal()}>
							<KTIcon className="fs-1" iconName="cross" />
						</div>
					</div>

					<div className="modal-body py-lg-10 px-lg-10">
						{/* begin::Form submit start*/}
						<form
							className="form w-100"
							onSubmit={formik.handleSubmit}
							noValidate
							id="kt_login_signin_form">
							{/* begin::Form group For Label Name*/}
							<div className="fv-row mb-8">
								<label className="form-label fs-6 fw-bolder text-dark">
									First Name
									<span className="text-danger px-2">*</span>
								</label>
								<input
									placeholder="Name"
									{...formik.getFieldProps("name")}
									className={clsx(
										"form-control bg-transparent",
										{ "is-invalid": formik.touched.name && formik.errors.name },
										{
											"is-valid": formik.touched.name && !formik.errors.name,
										}
									)}
									type="name"
									name="name"
									autoComplete="off"
								/>
								{formik.touched.name && formik.errors.name && (
									<div className="fv-plugins-message-container">
										<span role="alert">{formik.errors.name}</span>
									</div>
								)}
							</div>
							{/* end::Form group for Label Name*/}
							<div className="fv-row mb-8">
								<label className="form-label fs-6 fw-bolder text-dark">
									Last Name
								</label>
								<input
									placeholder="Last Name"
									{...formik.getFieldProps("lastName")}
									className={clsx(
										"form-control bg-transparent",
										{
											"is-invalid":
												formik.touched.lastName && formik.errors.lastName,
										},
										{
											"is-valid":
												formik.touched.lastName && !formik.errors.lastName,
										}
									)}
									type="name"
									name="lastName"
									autoComplete="off"
								/>
								{formik.touched.lastName && formik.errors.lastName && (
									<div className="fv-plugins-message-container">
										<span role="alert">{formik.errors.lastName}</span>
									</div>
								)}
							</div>

							{/* begin::Form group For Label Email*/}
							<div className="fv-row mb-8">
								<label className="form-label fs-6 fw-bolder text-dark">
									Email
								</label>
								<input
									placeholder="Email"
									{...formik.getFieldProps("email")}
									className={clsx(
										"form-control bg-transparent",
										{
											"is-invalid": formik.touched.email && formik.errors.email,
										},
										{
											"is-valid": formik.touched.email && !formik.errors.email,
										}
									)}
									type="email"
									name="email"
									autoComplete="off"
								/>
								{formik.touched.email && formik.errors.email && (
									<div className="fv-plugins-message-container">
										<span role="alert">{formik.errors.email}</span>
									</div>
								)}
							</div>
							{/* end::Form group for Label Email*/}

							<div className="fv-row mb-8">
								<label className="form-label fs-6 fw-bolder text-dark">
									Contact
								</label>
								<input
									placeholder="Contact"
									{...formik.getFieldProps("contact")}
									className={clsx(
										"form-control bg-transparent",
										{
											"is-invalid":
												formik.touched.contact && formik.errors.contact,
										},
										{
											"is-valid":
												formik.touched.contact && !formik.errors.contact,
										}
									)}
									type="number"
									name="contact"
									autoComplete="off"
								/>
								{formik.touched.contact && formik.errors.contact && (
									<div className="fv-plugins-message-container">
										<span role="alert">{formik.errors.contact}</span>
									</div>
								)}
							</div>

							<div className="fv-row mb-8">
								<label className="form-label fs-6 fw-bolder text-dark">
									Username
									<span className="text-danger px-2">*</span>
								</label>
								<input
									placeholder="Username"
									{...formik.getFieldProps("username")}
									className={clsx(
										"form-control bg-transparent",
										{
											"is-invalid":
												formik.touched.username && formik.errors.username,
										},
										{
											"is-valid":
												formik.touched.username && !formik.errors.username,
										}
									)}
									type="username"
									name="username"
									autoComplete="off"
								/>
								{formik.touched.username && formik.errors.username && (
									<div className="fv-plugins-message-container">
										<span role="alert">{formik.errors.username}</span>
									</div>
								)}
							</div>

							<div className="fv-row mb-8">
								<label className="form-label fs-6 fw-bolder text-dark">
									Password
									<span className="text-danger px-2">*</span>
								</label>
								<input
									placeholder="Password"
									{...formik.getFieldProps("password")}
									className={clsx(
										"form-control bg-transparent",
										{
											"is-invalid":
												formik.touched.password && formik.errors.password,
										},
										{
											"is-valid":
												formik.touched.password && !formik.errors.password,
										}
									)}
									type="password"
									name="password"
									autoComplete="off"
								/>
								{formik.touched.password && formik.errors.password && (
									<div className="fv-plugins-message-container">
										<span role="alert">{formik.errors.password}</span>
									</div>
								)}
							</div>
							<div className="fv-row mb-8">
								<label className="form-label fs-6 fw-bolder text-dark">
									Environment
									<span className="text-danger px-2">*</span>
								</label>
								<select
									placeholder="environment"
									{...formik.getFieldProps("environment")}
									style={{ height: "3.25rem" }}
									className={clsx(
										"form-control bg-transparent mb-2 ",
										{
											"is-invalid":
												formik.touched.environment && formik.errors.environment,
										},
										{
											"is-valid":
												formik.touched.environment &&
												!formik.errors.environment,
										}
									)}
									name="environment"
									onChange={handelEnviromentChange}
									value={environment}>
									<option value="" label="Select an environment" />
									<option value="staging" label="Staging" selected />
									<option value="production" label="Production" />
								</select>

								{formik.touched.environment && formik.errors.environment && (
									<div className="fv-plugins-message-container">
										<span role="alert">{formik.errors.environment}</span>
									</div>
								)}
							</div>

							{/* begin::Submit Action */}
							<div className="d-grid mb-10">
								{/* <div className='row g-2'> */}
								{/* <div className='col'>
        <button
          type='button'
          className='btn btn-secondary w-100'
          onClick={handleReset}
        >
          Reset
        </button>
      </div> */}
								<div className="col">
									<button
										type="submit"
										id="kt_sign_in_submit"
										className="btn btn-primary w-100"
										disabled={formik.isSubmitting || !formik.isValid}>
										{!loading && (
											<span className="indicator-label">Save</span>
										)}
										{loading && (
											<span
												className="indicator-progress"
												style={{ display: "block" }}>
												Please wait...
												<span className="spinner-border spinner-border-sm align-middle ms-2"></span>
											</span>
										)}
									</button>
								</div>
								{/* </div> */}
							</div>

							{/* end::Submit Action */}
						</form>
						{/* end::Form submit start*/}
					</div>
				</Modal>,
				modalsRoot
			)}
		</>
	);
};

export { AddNewClientModal };
